import { useApiClient } from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';

/**
 * Row + response shape for the per-scope content-health endpoints.
 * Mirrors `apps/tours/api/src/api/v1/data-health/health-checks/health-checks.types.ts`.
 */
export type HealthRowKind =
  | 'tour'
  | 'attraction'
  | 'theme'
  | 'tour-translation'
  | 'attraction-translation'
  | 'theme-translation';

export type HealthIssueType =
  | 'missing'
  | 'out-of-sync'
  | 'missing-translation';

export interface HealthIssue {
  type: HealthIssueType;
  field: string;
  label: string;
}

export interface HealthRowLanguage {
  id: string;
  code: string;
  name: string;
}

export interface HealthRowContext {
  attractionId?: string;
  themeId?: string;
  tourId?: string;
}

export interface HealthRow {
  kind: HealthRowKind;
  id: string;
  parentId?: string;
  parentName?: string;
  label: string;
  language?: HealthRowLanguage;
  issues: HealthIssue[];
  context?: HealthRowContext;
}

export interface HealthCheckResponse {
  success: boolean;
  data: {
    total: number;
    needsAttention: number;
    rows: HealthRow[];
  };
}

export type HealthScope = 'tour' | 'greaterArea' | 'attraction';
export type HealthCheck = 'content' | 'translations';

const SCOPE_SEGMENT: Record<HealthScope, string> = {
  tour: 'tours',
  greaterArea: 'greater-areas',
  attraction: 'attractions',
};

/**
 * Generic React Query hook for any (scope, check) health endpoint. Query key
 * includes both so navigating between sub-tabs doesn't share caches and an
 * invalidation after a regenerate targets the right check.
 */
export function useHealthCheck(
  scope: HealthScope,
  check: HealthCheck,
  scopeId: string | null | undefined,
) {
  const apiClient = useApiClient();

  return useQuery<HealthCheckResponse>({
    queryKey: ['HealthCheck', scope, check, scopeId ?? ''] as const,
    enabled: Boolean(scopeId),
    queryFn: () =>
      apiClient.get<HealthCheckResponse>(
        `/${SCOPE_SEGMENT[scope]}/${scopeId}/health/${check}`,
      ),
  });
}

export const healthCheckQueryKey = (
  scope: HealthScope,
  check: HealthCheck,
  scopeId: string,
) => ['HealthCheck', scope, check, scopeId] as const;
